import { supabase } from "../supabaseClient";

const normalizeCartItem = (item) => ({
  id: item.id || item.product_id || "",
  productId: item.product_id || item.productId || "",
  productSlug: item.product_slug || item.productSlug || "",
  name: item.product_name || item.name || "",
  selectedSize: item.selected_size || item.selectedSize || item.option_label || item.optionLabel || "",
  unitPrice: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
  optionPrice: Number(item.option_price ?? item.optionPrice ?? item.unit_price ?? item.price) || 0,
  optionLabel: item.selected_size || item.selectedSize || item.option_label || item.optionLabel || "",
  price: Number(item.unit_price ?? item.unitPrice ?? item.price) || 0,
  image: item.image || "",
  quantity: Math.max(1, Number(item.quantity) || 1),
});

const getAuthenticatedUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    console.error("Unable to resolve authenticated user for cart:", error);
    throw new Error(error.message || "Unable to verify signed-in user.");
  }

  if (!user) {
    throw new Error("User not logged in");
  }

  return user;
};

const buildInsertRecord = (userId, item) => ({
  user_id: userId,
  product_id: item.productId || item.id,
  product_slug: item.productSlug || "",
  product_name: item.name || "",
  option_label: item.selectedSize || item.optionLabel || "",
  selected_size: item.selectedSize || item.optionLabel || "",
  unit_price: Number(item.unitPrice ?? item.price) || 0,
  option_price: Number(item.optionPrice ?? item.unitPrice ?? item.price) || 0,
  price: Number(item.unitPrice ?? item.price) || 0,
  image: item.image || "",
  quantity: Math.max(1, Number(item.quantity) || 1),
});

const isRlsError = (error) =>
  error?.code === "42501" ||
  error?.status === 403 ||
  /row-level security|permission denied|policy/i.test(error?.message || "");

export const loadUserCart = async () => {
  const user = await getAuthenticatedUser();

  const { data, error } = await supabase
    .from("cart_items")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: true });

  if (error) {
    if (isRlsError(error)) {
      console.error("Supabase RLS blocked cart fetch:", error);
      throw new Error("Cart fetch failed due to Supabase RLS policy.");
    }

    throw new Error(error.message || "Unable to load cart.");
  }

  return (data || []).map(normalizeCartItem);
};

export const upsertUserCartItem = async (item) => {
  const user = await getAuthenticatedUser();

  const { data: existingItem, error: existingItemError } = await supabase
    .from("cart_items")
    .select("*")
    .eq("user_id", user.id)
    .eq("product_id", item.productId || item.id)
    .eq("selected_size", item.selectedSize || item.optionLabel || "")
    .maybeSingle();

  if (existingItemError) {
    if (isRlsError(existingItemError)) {
      console.error("Supabase RLS blocked cart lookup:", existingItemError);
      throw new Error("Cart lookup failed due to Supabase RLS policy.");
    }

    throw new Error(existingItemError.message || "Unable to check existing cart item.");
  }

  if (existingItem) {
    const quantityToAdd = Math.max(1, Number(item.quantity) || 1);
    const nextQuantity = Math.max(1, Number(existingItem.quantity) || 1) + quantityToAdd;
    const { data, error } = await supabase
      .from("cart_items")
      .update({
        quantity: nextQuantity,
        product_name: item.name || existingItem.product_name,
        product_slug: item.productSlug || existingItem.product_slug || "",
        option_label: item.selectedSize || item.optionLabel || existingItem.option_label || "",
        selected_size: item.selectedSize || item.optionLabel || existingItem.selected_size || "",
        unit_price: Number(item.unitPrice ?? item.price) || Number(existingItem.unit_price ?? existingItem.price) || 0,
        option_price:
          Number(item.optionPrice ?? item.unitPrice ?? item.price) ||
          Number(existingItem.option_price ?? existingItem.unit_price ?? existingItem.price) ||
          0,
        price: Number(item.unitPrice ?? item.price) || Number(existingItem.price) || 0,
        image: item.image || existingItem.image || "",
      })
      .eq("user_id", user.id)
      .eq("id", existingItem.id)
      .select()
      .single();
    if (error) {
      if (isRlsError(error)) {
        console.error("Supabase RLS blocked cart update:", error);
        throw new Error("Cart update failed due to Supabase RLS policy.");
      }

      throw new Error(error.message || "Unable to update cart item.");
    }

    return normalizeCartItem(data);
  }

  const record = buildInsertRecord(user.id, item);

  const { data, error } = await supabase
    .from("cart_items")
    .insert([record])
    .select()
    .single();
  if (error) {
    if (isRlsError(error)) {
      console.error("Supabase RLS blocked cart insert:", error);
      throw new Error("Cart insert failed due to Supabase RLS policy.");
    }

    throw new Error(error.message || "Unable to save cart item.");
  }

  return normalizeCartItem(data);
};

export const deleteUserCartItem = async (cartItemId) => {
  const user = await getAuthenticatedUser();

  const { error } = await supabase
    .from("cart_items")
    .delete()
    .eq("user_id", user.id)
    .eq("id", cartItemId);

  if (error) {
    if (isRlsError(error)) {
      console.error("Supabase RLS blocked cart delete:", error);
      throw new Error("Cart delete failed due to Supabase RLS policy.");
    }

    throw new Error(error.message || "Unable to remove cart item.");
  }
};

export const updateUserCartItemQuantity = async (cartItemId, quantity) => {
  const user = await getAuthenticatedUser();

  const { data, error } = await supabase
    .from("cart_items")
    .update({
      quantity: Math.max(1, Number(quantity) || 1),
    })
    .eq("user_id", user.id)
    .eq("id", cartItemId)
    .select()
    .single();

  if (error) {
    if (isRlsError(error)) {
      console.error("Supabase RLS blocked cart quantity update:", error);
      throw new Error("Cart quantity update failed due to Supabase RLS policy.");
    }

    throw new Error(error.message || "Unable to update cart quantity.");
  }

  return normalizeCartItem(data);
};

export const clearUserCartItems = async (cartItemIds) => {
  const user = await getAuthenticatedUser();

  const { error } = await supabase
    .from("cart_items")
    .delete()
    .eq("user_id", user.id)
    .in("id", cartItemIds);

  if (error) {
    if (isRlsError(error)) {
      console.error("Supabase RLS blocked cart clear:", error);
      throw new Error("Cart clear failed due to Supabase RLS policy.");
    }

    throw new Error(error.message || "Unable to clear cart items.");
  }
};
